export const ASSISTANT_PLATFORM_CHOICES = [
  { id: 'all_connected', label: 'All connected', prompt: 'all connected platforms' },
  { id: 'instagram', label: 'Instagram', prompt: 'Instagram' },
  { id: 'facebook', label: 'Facebook', prompt: 'Facebook' },
  { id: 'threads', label: 'Threads', prompt: 'Threads' },
  { id: 'telegram', label: 'Telegram', prompt: 'Telegram' },
  { id: 'tiktok', label: 'TikTok', prompt: 'TikTok' },
  { id: 'linkedin', label: 'LinkedIn', prompt: 'LinkedIn' },
  { id: 'x', label: 'X', prompt: 'X' },
];

const COPY = {
  en: {
    'tray.title': 'Quick actions',
    'tray.subtitle': 'Insert a draft request, then edit it.',
    'tray.channels': 'Channels',
    'tray.count': 'Count',
    'tray.topic': 'Topic',
    'tray.date': 'Date',
    'tray.reference': 'Link or reference',
    'tray.run': 'Ask AI',
    'tray.pickChannel': 'Choose at least one channel.',
    'action.create_post.label': 'Create post',
    'action.create_post.description': 'Prepare one review-ready draft.',
    'action.copy_rewrite_post.label': 'Copy or rewrite post',
    'action.copy_rewrite_post.description': 'Use the open or latest post as source.',
    'action.generate_week.label': 'Week of posts',
    'action.generate_week.description': 'Prepare 7 review-ready drafts.',
    'action.generate_month.label': 'Month of posts',
    'action.generate_month.description': 'Prepare 30 review-ready drafts.',
    'action.generate_date.label': 'Post for date',
    'action.generate_date.description': 'Prepare one dated draft.',
    'action.from_reference.label': 'From link/photo',
    'action.from_reference.description': 'Use a URL or attached reference.',
    'action.connect_social.label': 'Connect account',
    'action.connect_social.description': 'Open a provider setup flow.',
    'action.regenerate_plan.label': 'Regenerate plan',
    'action.regenerate_plan.description': 'Refresh the Content Plan with guidance.',
  },
};

const ACTIONS = [
  {
    id: 'connect_social',
    labelKey: 'action.connect_social.label',
    descriptionKey: 'action.connect_social.description',
    icon: 'plug',
    screens: ['dashboard', 'brand', 'settings', 'social', 'create', 'content'],
    fields: [],
    actionType: 'social.connect_account',
    template: 'Connect another social account',
    params: {},
    prompt: () => 'Connect another social account. Ask me which network if needed, then open the approved setup flow.',
  },
  {
    id: 'regenerate_plan',
    labelKey: 'action.regenerate_plan.label',
    descriptionKey: 'action.regenerate_plan.description',
    icon: 'refresh',
    screens: ['dashboard', 'brand', 'create', 'content'],
    fields: [],
    actionType: 'strategy.regenerate_content_plan',
    template: 'Regenerate my Content Plan focusing on [what should change]',
    params: {},
    prompt: () => 'Regenerate my Content Plan from the current Brand Profile and publishing rules. Ask for focus if needed.',
  },
  {
    id: 'create_post',
    labelKey: 'action.create_post.label',
    descriptionKey: 'action.create_post.description',
    icon: 'edit',
    screens: ['dashboard', 'content', 'create', 'brand', 'scheduler'],
    fields: ['topic', 'optionalPlatformTargets'],
    actionType: 'content.generate_single_draft',
    template: 'Create one Instagram post about [topic] using [reference]',
    params: {},
    prompt: ({ topic, platformPhrase }) => `Create one review-ready draft${topicPhrase(topic)}${optionalPlatformPhrase(platformPhrase)}. Save it for review. Do not schedule or publish it.`,
  },
  {
    id: 'copy_rewrite_post',
    labelKey: 'action.copy_rewrite_post.label',
    descriptionKey: 'action.copy_rewrite_post.description',
    icon: 'copy',
    screens: ['dashboard', 'content', 'create', 'preview', 'editor', 'carousel'],
    fields: ['platformTargets'],
    actionType: 'content.rewrite_for_platform',
    template: 'Rewrite this post for LinkedIn',
    params: { platform: 'linkedin' },
    prompt: ({ platformPhrase }) => `Create ${isPluralPlatformPhrase(platformPhrase) ? 'drafts' : 'a draft'} by rewriting or adapting the selected post into ${platformPhrase}. If no post is selected, use my latest owned post. Save for review only. Do not schedule or publish.`,
  },
  {
    id: 'generate_week',
    labelKey: 'action.generate_week.label',
    descriptionKey: 'action.generate_week.description',
    icon: 'calendar',
    screens: ['dashboard', 'content', 'create', 'brand', 'scheduler'],
    fields: ['topic', 'optionalPlatformTargets'],
    actionType: 'content.generate_paid_batch',
    template: 'Generate a week of Instagram posts about [topic]',
    params: { planLengthDays: 7, count: 7, platform: 'instagram' },
    prompt: ({ topic, platformPhrase }) => `Prepare a 7-day weekly content batch${topicPhrase(topic)}${optionalPlatformPhrase(platformPhrase)}. Save 7 review-ready drafts. Do not schedule or publish them.`,
  },
  {
    id: 'generate_month',
    labelKey: 'action.generate_month.label',
    descriptionKey: 'action.generate_month.description',
    icon: 'calendar',
    screens: ['dashboard', 'content', 'create', 'brand', 'scheduler'],
    fields: ['topic', 'optionalPlatformTargets'],
    actionType: 'content.generate_paid_batch',
    template: 'Generate a month of Instagram posts about [topic]',
    params: { planLengthDays: 30, count: 30, platform: 'instagram' },
    prompt: ({ topic, platformPhrase }) => `Prepare a 30-day monthly content batch${topicPhrase(topic)}${optionalPlatformPhrase(platformPhrase)}. Save 30 review-ready drafts. Do not schedule or publish them.`,
  },
  {
    id: 'generate_date',
    labelKey: 'action.generate_date.label',
    descriptionKey: 'action.generate_date.description',
    icon: 'calendar',
    screens: ['dashboard', 'content', 'create', 'brand', 'scheduler'],
    fields: ['topic', 'targetDate', 'optionalPlatformTargets'],
    actionType: 'content.generate_single_draft',
    template: 'Generate a post for [date] about [topic]',
    params: {},
    prompt: ({ topic, targetDate, platformPhrase }) => `Create one review-ready draft${topicPhrase(topic)} for ${targetDate || 'the date I will choose'}${optionalPlatformPhrase(platformPhrase)}. Save it for review. Do not schedule or publish it.`,
  },
  {
    id: 'from_reference',
    labelKey: 'action.from_reference.label',
    descriptionKey: 'action.from_reference.description',
    icon: 'image',
    screens: ['dashboard', 'content', 'create', 'brand', 'preview', 'editor', 'carousel'],
    fields: ['referenceText', 'optionalPlatformTargets'],
    actionType: 'content.create_post_from_reference',
    template: 'Generate a post from this link: [url]',
    params: {},
    prompt: ({ referenceText, platformPhrase }) => `Create one review-ready draft from ${referenceText?.trim() || 'my latest attached reference'}${optionalPlatformPhrase(platformPhrase)}. If this is a URL, use it as reference context. Do not schedule or publish it.`,
  },
];

export function assistantActionsForContext({ currentScreen, selectedEntity }) {
  const screen = currentScreen || 'default';
  const hasContent = selectedEntity?.type === 'contentItem' && selectedEntity?.id;
  return ACTIONS.filter(action => {
    if (action.requiresContent && !hasContent) return false;
    if (Array.isArray(action.screens) && !action.screens.includes(screen)) return false;
    return true;
  }).sort((a, b) => actionContextPriority(screen, a) - actionContextPriority(screen, b)).slice(0, 6);
}

function actionContextPriority(screen, action) {
  if (screen === 'social' && action.id === 'connect_social') return -20;
  if (screen === 'brand' && action.id === 'regenerate_plan') return -20;
  if (screen === 'brand' && action.id === 'connect_social') return -10;
  if (action.id === 'connect_social' || action.id === 'regenerate_plan') return 20;
  return 0;
}

export function assistantActionText(action, name, locale = 'en') {
  const key = name === 'description' ? action.descriptionKey : action.labelKey;
  return COPY[locale]?.[key] || COPY.en[key] || key;
}

export function assistantTrayText(key, locale = 'en') {
  return COPY[locale]?.[key] || COPY.en[key] || key;
}

export function assistantActionLocale() {
  if (typeof navigator === 'undefined') return 'en';
  const language = String(navigator.language || '').toLowerCase();
  if (language.startsWith('uk')) return 'uk';
  if (language.startsWith('ru')) return 'ru';
  return 'en';
}

export function defaultAssistantActionValues(action) {
  const values = {};
  if (action?.fields?.includes('platformTargets')) values.platformTargets = [];
  if (action?.fields?.includes('optionalPlatformTargets')) values.platformTargets = [];
  if (action?.fields?.includes('count')) values.count = action.defaultCount || 3;
  if (action?.fields?.includes('topic')) values.topic = '';
  if (action?.fields?.includes('targetDate')) values.targetDate = '';
  if (action?.fields?.includes('referenceText')) values.referenceText = '';
  return values;
}

export function assistantActionReady(action, values = {}) {
  if (!action) return false;
  if (action.fields?.includes('platformTargets') && !selectedPlatformTargets(values).length) return false;
  return true;
}

export function buildAssistantActionPrompt(action, values = {}) {
  if (!assistantActionReady(action, values)) return '';
  const platformPhrase = platformPromptPhrase(selectedPlatformTargets(values));
  const count = boundedCount(values.count, action?.defaultCount || 3);
  return action.prompt({
    platformPhrase,
    count,
    topic: String(values.topic || '').trim(),
    targetDate: String(values.targetDate || '').trim(),
    referenceText: String(values.referenceText || '').trim(),
  });
}

export function buildAssistantActionDraft(action) {
  if (!action) return null;
  return {
    text: action.template || assistantActionText(action, 'label'),
    metadata: {
      actionId: action.id,
      type: action.actionType,
      label: assistantActionText(action, 'label'),
      params: { ...(action.params || {}) },
    },
  };
}

export function togglePlatformTarget(current = [], target) {
  if (target === 'all_connected') {
    return current.includes(target) ? [] : [target];
  }
  const withoutConnected = current.filter(item => item !== 'all_connected');
  if (withoutConnected.includes(target)) {
    return withoutConnected.filter(item => item !== target);
  }
  return [...withoutConnected, target];
}

export function selectedPlatformTargets(values = {}) {
  return Array.isArray(values.platformTargets) ? values.platformTargets.filter(Boolean) : [];
}

function platformPromptPhrase(targets) {
  if (!targets.length) return '';
  if (targets.includes('all_connected')) return 'all connected platforms';
  const choicesById = new Map(ASSISTANT_PLATFORM_CHOICES.map(choice => [choice.id, choice]));
  const labels = targets.map(target => choicesById.get(target)?.prompt).filter(Boolean);
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

function optionalPlatformPhrase(value) {
  return value ? ` for ${value}` : '';
}

function topicPhrase(value) {
  return value ? ` about ${value}` : '';
}

function isPluralPlatformPhrase(value) {
  return value === 'all connected platforms' || value.includes(' and ') || value.includes(',');
}

function boundedCount(value, fallback) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(1, Math.min(6, Math.trunc(numeric)));
}
